import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  lazy,
  Suspense,
} from "react";
import { Box } from "@mui/material";
import { ViewAgendaOutlined, GridViewOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import Header from "./header";
import "./redesign.css";
import Blog from "../Blog/blogCard";
import BottomNavbar from "../BottomNavigation/bottomNavigation";
import { setBlogsData } from "../../store/slices/blogSlice";
import HomeLoading from "../../helpers/homeLoading";
import ProfilePopup from "./ProfilePopup";
import noBlogsImage from "../../assets/noblogs.png";
// import AdminDashboard from "../Sidebar/AdminDashboard";
import {
  getBlogsByCategoryApi,
  normalizeBlogsCollectionResponse,
} from "../../providers/blogProvider";
import { getWinnerOfTheMonth } from "../../providers/adminProvider";
import { getProfileDashboardApi } from "../../providers/userProvider";
import WinnerTicker from "../WinnerTicker";
import CategoryTabs from "../Sidebar/CategoryTabs";
import { useNavigate } from "react-router-dom";
import {
  formatDate,
  getInitials,
  getReadTime,
} from "../../utilities/commonUtils";
import Footer from "./footer";
import OptimizedImage from "../common/OptimizedImage";
import { trackEvent } from "../../analytics/analyticsClient";

const AdminDashboard = lazy(() => import("../Sidebar/AdminDashboard"));
const QuickStoriesProvider = lazy(() =>
  import("../QuickStories/QuickStories").then((module) => ({
    default: module.QuickStoriesProvider,
  })),
);
const QuickStoriesStrip = lazy(() =>
  import("../QuickStories/QuickStories").then((module) => ({
    default: module.QuickStoriesStrip,
  })),
);
const QuickStoriesSidebarWidget = lazy(() =>
  import("../QuickStories/QuickStories").then((module) => ({
    default: module.QuickStoriesSidebarWidget,
  })),
);

const BLOGS_PAGE_LIMIT = 10;

const mergeBlogs = (existingBlogs = [], incomingBlogs = []) => {
  const blogMap = new Map();

  [...existingBlogs, ...incomingBlogs].forEach((blog) => {
    if (blog?._id) {
      blogMap.set(blog._id, blog);
    }
  });

  return Array.from(blogMap.values());
};

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(false);
  const [apiStatus, setApiStatus] = useState("INITIAL");
  const blogs = useSelector((state) => state.blogs.blogs);
  const [updatedBlogs, setUpdatedBlogs] = useState(blogs);
  const [searchInput, setSearchInput] = useState("");
  const [category, setCategory] = useState("All");
  const [winnerDetails, setWinnerDetails] = useState([]);
  const [view, setView] = useState("list");
  const [draftBlog, setDraftBlog] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreBlogs, setHasMoreBlogs] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);
  const isLoadingMoreRef = useRef(false);

  useEffect(() => {
    setUpdatedBlogs(blogs);
  }, [blogs]);

  useEffect(() => {
    let isMounted = true;

    const fetchBlogsByCategory = async () => {
      setApiStatus("INITIAL");
      setHasMoreBlogs(false);
      setCurrentPage(1);
      dispatch(setBlogsData([]));

      const response = await getBlogsByCategoryApi(category, {
        page: 1,
        limit: BLOGS_PAGE_LIMIT,
      });

      if (!isMounted) {
        return;
      }

      if (response?.status === 200) {
        const normalizedResponse = normalizeBlogsCollectionResponse(
          response.data,
          1,
          BLOGS_PAGE_LIMIT,
        );

        setApiStatus("SUCCESS");
        setCurrentPage(normalizedResponse.page);
        setHasMoreBlogs(normalizedResponse.hasMore);
        dispatch(setBlogsData(normalizedResponse.blogs));
        trackEvent("feed_loaded", {
          category,
          resultCount: normalizedResponse.blogs.length,
          page: 1,
        });
        return;
      }

      setApiStatus("FAILURE");
    };

    fetchBlogsByCategory();

    return () => {
      isMounted = false;
    };
  }, [category, dispatch]);

  useEffect(() => {
    trackEvent("category_selected", { category });
  }, [category]);

  useEffect(() => {
    const getWinnerDetails = async () => {
      const response = await getWinnerOfTheMonth();

      if (response && !response?.data?.message) {
        setWinnerDetails(response?.data || []);
      } else {
        setWinnerDetails([]);
      }
    };

    getWinnerDetails();
  }, []);

  useEffect(() => {
    const loadDraftBlog = async () => {
      const response = await getProfileDashboardApi();

      if (response?.status === 200) {
        setDraftBlog(response.data?.draftBlog || null);
      }
    };

    loadDraftBlog();
  }, []);

  useEffect(() => {
    if (!searchInput) {
      setUpdatedBlogs(blogs);
      return;
    }

    const query = searchInput.toLowerCase();
    const filteredBlogs = blogs.filter((blog) => {
      const blogMonth = new Date(blog.date)
        .toLocaleString("default", { month: "long" })
        .toLowerCase();

      return (
        blog?.username?.toLowerCase().includes(query) ||
        blog?.title?.toLowerCase().includes(query) ||
        blog?.category?.toLowerCase().includes(query) ||
        blogMonth.includes(query)
      );
    });

    setUpdatedBlogs(filteredBlogs);
  }, [blogs, searchInput]);

  useEffect(() => {
    if (!searchInput.trim()) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      trackEvent("search_performed", {
        query: searchInput.trim(),
        resultCount: updatedBlogs.length,
        category,
      });
    }, 700);

    return () => window.clearTimeout(timer);
  }, [category, searchInput, updatedBlogs.length]);

  useEffect(() => {
    if (apiStatus !== "SUCCESS" || !hasMoreBlogs || isLoadingMore) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      async (entries) => {
        const [entry] = entries;

        if (!entry?.isIntersecting) {
          return;
        }

        if (isLoadingMoreRef.current) {
          return;
        }

        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);
        const nextPage = currentPage + 1;
        const response = await getBlogsByCategoryApi(category, {
          page: nextPage,
          limit: BLOGS_PAGE_LIMIT,
        });

        if (response?.status === 200) {
          const normalizedResponse = normalizeBlogsCollectionResponse(
            response.data,
            nextPage,
            BLOGS_PAGE_LIMIT,
          );

          dispatch(setBlogsData(mergeBlogs(blogs, normalizedResponse.blogs)));
          setCurrentPage(normalizedResponse.page);
          setHasMoreBlogs(normalizedResponse.hasMore);
          trackEvent("feed_paginated", {
            category,
            page: normalizedResponse.page,
            resultCount: normalizedResponse.blogs.length,
          });
        } else {
          setHasMoreBlogs(false);
        }

        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      },
      {
        rootMargin: "240px 0px",
      },
    );

    const currentTarget = loadMoreRef.current;

    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }

      observer.disconnect();
    };
  }, [
    apiStatus,
    blogs,
    category,
    currentPage,
    dispatch,
    hasMoreBlogs,
    isLoadingMore,
  ]);

  const featuredBlog = updatedBlogs[0];
  const listBlogs = updatedBlogs.slice(featuredBlog ? 1 : 0);

  const spotlightWriters = useMemo(() => {
    const counts = updatedBlogs.reduce((acc, blog) => {
      const name = blog?.username || "Anonymous";
      const role = blog?.userrole || blog?.category || "Contributor";

      if (!acc[name]) {
        acc[name] = {
          name,
          role,
          totalBlogs: 0,
        };
      }

      acc[name].totalBlogs += 1;
      return acc;
    }, {});

    return Object.values(counts)
      .sort((a, b) => b.totalBlogs - a.totalBlogs)
      .slice(0, 3);
  }, [updatedBlogs]);

  const handleClose = () => {
    setProfile(false);
  };

  const renderFeaturedBlog = () => {
    if (!featuredBlog) return null;

    return (
      <Box
        className="home-featured-card"
        onClick={() => {
          trackEvent("blog_card_clicked", {
            blogId: featuredBlog._id,
            blogTitle: featuredBlog.title,
            category: featuredBlog.category,
            position: "featured",
          });
          navigate(`/blogs/${featuredBlog._id}`);
        }}
      >
        <OptimizedImage
          className="home-featured-image"
          src={featuredBlog.blogImage}
          alt={featuredBlog.title}
          width={640}
          height={400}
          widths={[640, 960, 1280]}
          sizes="(max-width: 767px) 100vw, (max-width: 1280px) 50vw, 640px"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="home-featured-body">
          <div className="home-featured-label">
            ✦ Editor&apos;s Pick · {featuredBlog.category || "Latest"}
          </div>
          <h2 className="home-featured-title">{featuredBlog.title}</h2>
          <p className="home-featured-excerpt">
            {featuredBlog.description ||
              "Read the latest story from AAPMOR Blogs."}
          </p>
          <div className="home-featured-meta">
            <div className="home-author-chip">
              <div className="home-author-dot">
                {getInitials(featuredBlog.username)}
              </div>
              <div className="home-author-name">
                {featuredBlog.username || "Anonymous"}
              </div>
            </div>
            <div className="home-meta-dot" />
            <div className="home-meta-date">
              {formatDate(featuredBlog.date)} · {getReadTime(featuredBlog)}
            </div>
            <button className="home-read-btn" type="button">
              Read →
            </button>
          </div>
        </div>
      </Box>
    );
  };

  const renderNoBlogsView = () => (
    <div className="home-empty-state">
      <div className="home-empty-card">
        <img
          className="home-empty-image"
          src={noBlogsImage}
          alt="no-blog"
          width="420"
          height="280"
          loading="lazy"
          decoding="async"
        />
        <div className="home-empty-copy">
          No matching blogs found. Please adjust your search criteria or{" "}
          <a className="home-empty-link" href="/">
            reload
          </a>{" "}
          all blogs.
        </div>
      </div>
    </div>
  );

  const renderSuccessView = () => {
    if (!updatedBlogs.length) {
      return renderNoBlogsView();
    }

    return (
      <>
        {renderFeaturedBlog()}
        <div
          className={`home-blog-list ${
            view === "grid" ? "home-blog-list-grid" : ""
          }`}
        >
          {listBlogs.map((blogItem) => (
            <Blog blogDetails={blogItem} key={blogItem._id} view={view} />
          ))}
        </div>
        {(hasMoreBlogs || isLoadingMore) && (
          <>
            <div
              className="home-load-more-anchor"
              ref={loadMoreRef}
              aria-hidden="true"
            />
            <div className="home-load-more-state">
              {isLoadingMore ? "Loading more blogs..." : "Scroll to load more"}
            </div>
          </>
        )}
      </>
    );
  };

  const renderFailureView = () => (
    <div className="home-error-state">
      Unable to load blogs. Please try again in a moment.
    </div>
  );

  const renderLoadingView = () => (
    <div className="home-loading-state">
      <HomeLoading />
    </div>
  );

  const renderBlogsApi = () => {
    switch (apiStatus) {
      case "INITIAL":
        return renderLoadingView();
      case "SUCCESS":
        return renderSuccessView();
      case "FAILURE":
        return renderFailureView();
      default:
        return null;
    }
  };

  return (
    <div className="home-page">
      {winnerDetails?.length > 0 && (
        <WinnerTicker winnerDetails={winnerDetails} />
      )}
      <Header setSearchInput={setSearchInput} setProfile={setProfile} />
      {/* <QuickStoriesProvider>
        <div className="home-layout">
          <main className="home-feed-wrap">
            <QuickStoriesStrip />
            <CategoryTabs category={category} setCategory={setCategory} />
            <div className="home-section-header">
              <h2 className="home-section-title">Latest Stories</h2>
              <div className="home-view-toggle" aria-hidden="true">
                <button
                  className={`home-view-btn ${view === "list" ? "active" : ""}`}
                  type="button"
                  onClick={() => setView("list")}
                >
                  <ViewAgendaOutlined sx={{ fontSize: 18 }} />
                </button>

                <button
                  className={`home-view-btn ${view === "grid" ? "active" : ""}`}
                  type="button"
                  onClick={() => setView("grid")}
                >
                  <GridViewOutlined sx={{ fontSize: 18 }} />
                </button>
              </div>
            </div>
            {renderBlogsApi()}
          </main>
          <aside className="home-sidebar">
            <QuickStoriesSidebarWidget />
            <Suspense fallback={null}>
              <AdminDashboard
                blogs={updatedBlogs}
                draftBlog={draftBlog}
                winnerDetails={winnerDetails}
                spotlightWriters={spotlightWriters}
              />
            </Suspense>
          </aside>
        </div>
      </QuickStoriesProvider> */}
      <Suspense fallback={null}>
        <QuickStoriesProvider>
          <div className="home-layout">
            <main className="home-feed-wrap">
              <QuickStoriesStrip />
              <CategoryTabs category={category} setCategory={setCategory} />
              <div className="home-section-header">
                <h2 className="home-section-title">Latest Stories</h2>
                <div className="home-view-toggle" aria-hidden="true">
                  <button
                    className={`home-view-btn ${view === "list" ? "active" : ""}`}
                    type="button"
                    onClick={() => setView("list")}
                  >
                    <ViewAgendaOutlined sx={{ fontSize: 18 }} />
                  </button>

                  <button
                    className={`home-view-btn ${view === "grid" ? "active" : ""}`}
                    type="button"
                    onClick={() => setView("grid")}
                  >
                    <GridViewOutlined sx={{ fontSize: 18 }} />
                  </button>
                </div>
              </div>
              {renderBlogsApi()}
            </main>

            <aside className="home-sidebar">
              <QuickStoriesSidebarWidget />
              <Suspense fallback={null}>
                <AdminDashboard
                  blogs={updatedBlogs}
                  draftBlog={draftBlog}
                  winnerDetails={winnerDetails}
                  spotlightWriters={spotlightWriters}
                />
              </Suspense>
            </aside>
          </div>
        </QuickStoriesProvider>
      </Suspense>
      {profile && (
        <ProfilePopup
          profile={profile}
          handleClose={handleClose}
          setProfile={setProfile}
        />
      )}
      <Footer />
      <BottomNavbar />
    </div>
  );
};

export default Home;
